import { TranslateModule } from '@ngx-translate/core';
import {
  ApplicationRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  inject,
  input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'lib-tooltip',
  imports: [TranslateModule],
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent implements OnDestroy {
  readonly container = input<HTMLElement | null>(null);
  readonly tooltip = input<string | undefined>('');
  readonly position = input<'top' | 'bottom' | 'left' | 'right'>('top');
  readonly delay = input(200);
  readonly visible = input(false);

  @ViewChild('tooltipBoxTemplate', { static: true }) tooltipTemplate!: TemplateRef<unknown>;
  @ViewChild('tooltipTrigger', { static: true }) tooltipTrigger!: ElementRef<HTMLElement>;

  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private positionFrameId: number | null = null;
  private tooltipRef: EmbeddedViewRef<unknown> | null = null;
  private triggerObserver: IntersectionObserver | null = null;

  private readonly renderer = inject(Renderer2);
  private readonly appRef = inject(ApplicationRef);

  showTooltip() {
    // mouseenter and focusin both land here: keep one pending show and one node per trigger
    if (!this.tooltip() || this.showTimeoutId !== null || this.tooltipRef) {
      return;
    }
    this.showTimeoutId = setTimeout(() => {
      this.showTimeoutId = null;
      this.createTooltip();
    }, this.delay());
  }

  hideTooltip() {
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
    if (this.positionFrameId !== null) {
      cancelAnimationFrame(this.positionFrameId);
      this.positionFrameId = null;
    }
    this.triggerObserver?.disconnect();
    this.triggerObserver = null;

    if (this.tooltipRef) {
      const parent = this.container() ?? document.body;
      this.appRef.detachView(this.tooltipRef);
      this.tooltipRef.rootNodes.forEach((node) => this.renderer.removeChild(parent, node));
      this.tooltipRef.destroy();
      this.tooltipRef = null;
    }
  }

  private createTooltip() {
    const trigger = this.tooltipTrigger.nativeElement;
    if (!trigger.isConnected) {
      return;
    }

    this.tooltipRef = this.tooltipTemplate.createEmbeddedView(null);
    this.appRef.attachView(this.tooltipRef);

    const domElem = this.tooltipRef.rootNodes[0] as HTMLElement;
    const parent = this.container() ?? document.body;
    this.renderer.appendChild(parent, domElem);

    // Wait a frame so the tooltip's own size is known before positioning it
    this.positionFrameId = requestAnimationFrame(() => {
      this.positionFrameId = null;
      const hostRect = trigger.getBoundingClientRect();
      // A trigger that left the page measures as an empty rect, which would place the tooltip top-left
      if (!trigger.isConnected || (hostRect.width === 0 && hostRect.height === 0)) {
        this.hideTooltip();
        return;
      }
      this.setPosition(domElem, hostRect);
      this.watchTrigger(trigger);
    });
  }

  // Removing or hiding the trigger fires neither mouseleave nor focusout
  private watchTrigger(trigger: HTMLElement) {
    this.triggerObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => !entry.isIntersecting)) {
        this.hideTooltip();
      }
    });
    this.triggerObserver.observe(trigger);
  }

  private setPosition(domElem: HTMLElement, hostRect: DOMRect) {
    const tooltipRect = domElem.getBoundingClientRect();
    const padding = 8;
    const offset = 6; // Distance between tooltip and trigger element

    // Get scroll positions
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate absolute positions (relative to document, not viewport)
    const hostTop = hostRect.top + scrollY;
    const hostLeft = hostRect.left + scrollX;
    const hostBottom = hostRect.bottom + scrollY;
    const hostRight = hostRect.right + scrollX;

    let top = 0;
    let left = 0;

    switch (this.position()) {
      case 'top':
        top = hostTop - tooltipRect.height - offset;
        left = hostLeft + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = hostBottom + offset;
        left = hostLeft + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = hostTop + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostLeft - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostTop + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostRight + offset;
        break;
    }

    // Get viewport boundaries relative to document
    const viewportTop = scrollY + padding;
    const viewportBottom = scrollY + window.innerHeight - padding;
    const viewportLeft = scrollX + padding;
    const viewportRight = scrollX + window.innerWidth - padding;

    // Clamp to viewport boundaries
    left = Math.max(viewportLeft, Math.min(left, viewportRight - tooltipRect.width));
    top = Math.max(viewportTop, Math.min(top, viewportBottom - tooltipRect.height));

    // Set the final styles to position the tooltip
    this.renderer.setStyle(domElem, 'position', 'absolute');
    this.renderer.setStyle(domElem, 'top', `${top}px`);
    this.renderer.setStyle(domElem, 'left', `${left}px`);
    this.renderer.setStyle(domElem, 'z-index', '1000');
  }

  ngOnDestroy() {
    this.hideTooltip();
  }
}
